import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { User } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EmployeeAvatarProps {
  name: string;
  photoUrl?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeClasses = {
  sm: 'h-8 w-8 text-xs',
  md: 'h-10 w-10 text-sm',
  lg: 'h-12 w-12 text-base',
  xl: 'h-16 w-16 text-lg',
};

/**
 * EmployeeAvatar component displays a circular avatar with the employee's photo
 * or initials as a fallback. Optimized for performance and accessibility.
 */
export function EmployeeAvatar({ 
  name, 
  photoUrl, 
  size = 'md',
  className 
}: EmployeeAvatarProps) {
  // Generate initials from name (first letter of first and last name)
  const getInitials = (fullName: string): string => {
    const parts = fullName.trim().split(' ');
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  const initials = getInitials(name);

  return (
    <Avatar className={cn(sizeClasses[size], className)}>
      {photoUrl && (
        <AvatarImage 
          src={photoUrl} 
          alt={`${name}'s profile photo`}
          loading="lazy"
        />
      )}
      <AvatarFallback 
        className="bg-primary/10 text-primary font-semibold"
        aria-label={`${name}'s avatar`}
      >
        {initials || <User className="h-1/2 w-1/2" />}
      </AvatarFallback>
    </Avatar>
  );
}
